import { useMemo } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useTranslation } from "@repo/core";
import LoadingTurnover from "./LoadingTurnover";
import MissingTurnover from "./MissingTurnover";

interface TurnoverDataPoint {
  date: string;
  turnover: number;
}

interface TurnoverChartProps {
  turnovers: TurnoverDataPoint[];
  meanTurnover?: number;
  statistics?: {
    mean: number;
    median: number;
    std: number;
    min: number;
    max: number;
    count: number;
  };
  isLoading?: boolean;
  className?: string;
}

/**
 * Turnover chart component showing turnover time series and statistics
 */
export const TurnoverChart = ({
  turnovers,
  meanTurnover: _meanTurnover, // Unused but kept for API compatibility
  statistics,
  isLoading = false,
  className = "",
}: TurnoverChartProps) => {
  const { t, i18n } = useTranslation();

  // Prepare chart data in the format ApexCharts expects
  const chartSeries =
    turnovers && turnovers.length > 0
      ? [
          {
            name: t("turnover.seriesName"),
            data: turnovers.map((point) => {
              const date = new Date(point.date);
              const timestamp = isNaN(date.getTime())
                ? Date.now()
                : date.getTime();
              const value =
                typeof point.turnover === "number" && !isNaN(point.turnover)
                  ? point.turnover * 100 // Convert to percentage
                  : 0;

              return {
                x: timestamp,
                y: value,
              };
            }),
          },
        ]
      : [];

  // Chart configuration
  const chartOptions: ApexOptions = useMemo(() => {
    return {
      chart: {
        type: "line",
        height: 350,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        background: "transparent",
        animations: {
          enabled: false,
        },
        defaultLocale: i18n.language === "cs" ? "cs" : "en",
        locales: [
          {
            name: "en",
            options: {
              months: [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
              ],
              shortMonths: [
                "Jan",
                "Feb",
                "Mar",
                "Apr",
                "May",
                "Jun",
                "Jul",
                "Aug",
                "Sep",
                "Oct",
                "Nov",
                "Dec",
              ],
            },
          },
          {
            name: "cs",
            options: {
              months: [
                "Leden",
                "Únor",
                "Březen",
                "Duben",
                "Květen",
                "Červen",
                "Červenec",
                "Srpen",
                "Září",
                "Říjen",
                "Listopad",
                "Prosinec",
              ],
              shortMonths: [
                "Led",
                "Úno",
                "Bře",
                "Dub",
                "Kvě",
                "Čvn",
                "Čvc",
                "Srp",
                "Zář",
                "Říj",
                "Lis",
                "Pro",
              ],
            },
          },
        ],
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      colors: ["#10B981"], // Green color
      xaxis: {
        type: "datetime",
        labels: {
          format: "MMM yyyy",
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => `${value.toFixed(1)}%`,
        },
        min: 0,
        max: 100,
      },
      grid: {
        borderColor: "#e5e7eb",
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
      tooltip: {
        y: {
          formatter: (value) => `${value.toFixed(2)}%`,
        },
      },
    };
  }, [i18n.language, t]);

  if (isLoading) {
    return <LoadingTurnover className={className} />;
  }

  if (!turnovers || turnovers.length === 0) {
    return <MissingTurnover className={className} />;
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
      data-tour="turnover-chart"
    >
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        {t("turnover.title")}
      </h2>

      <hr className="border-gray-200 mb-6" />

      {/* Statistics Summary */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">{t("turnover.mean")}</p>
            <p className="text-lg font-semibold text-gray-900">
              {(statistics.mean * 100).toFixed(1)}%
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">{t("turnover.median")}</p>
            <p className="text-lg font-semibold text-gray-900">
              {(statistics.median * 100).toFixed(1)}%
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">{t("turnover.stdDev")}</p>
            <p className="text-lg font-semibold text-gray-900">
              {(statistics.std * 100).toFixed(1)}%
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">{t("turnover.min")}</p>
            <p className="text-lg font-semibold text-gray-900">
              {(statistics.min * 100).toFixed(1)}%
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">{t("turnover.max")}</p>
            <p className="text-lg font-semibold text-gray-900">
              {(statistics.max * 100).toFixed(1)}%
            </p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">
              {t("turnover.rebalances")}
            </p>
            <p className="text-lg font-semibold text-gray-900">
              {statistics.count}
            </p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="h-96">
        {chartSeries.length > 0 && chartSeries[0].data.length > 0 ? (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height="100%"
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <div className="text-4xl mb-2">📊</div>
              <p>No chart data available</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TurnoverChart;
